import {
  Directive,
  effect,
  ElementRef,
  inject,
  input,
  InputSignal,
  NgZone,
  OnDestroy,
  OnInit,
  output,
  Renderer2,
} from '@angular/core';
import { v4 as uuid } from 'uuid';
import { documentOf } from '../anchor-document.util';

export type TYPosition = 'start' | 'center-start' | 'center' | 'center-end' | 'end' | number;
export type TXPosition = 'start' | 'center' | 'end' | number;

@Directive({
  selector: '[toggleElement]',
  standalone: true,
  exportAs: 'toggleElementDirective',
})
export class ToggleElementDirective implements OnInit, OnDestroy {
  private readonly el: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly renderer: Renderer2 = inject(Renderer2);
  private readonly ngZone: NgZone = inject(NgZone);
  private readonly refToggleElementId = uuid();

  /**
   * @param zIndexClass The class to apply the z-index.
   */
  public readonly zIndexClass: InputSignal<string> = input<string>();

  /**
   * @param anchor The anchor element to toggle the element.
   */
  public readonly anchor: InputSignal<HTMLElement> = input<HTMLElement>();

  /**
   * @param xAxis The x-axis position of the element.
   * @default 'start'
   */
  public xAxis: InputSignal<TXPosition> = input<TXPosition>('start');

  /**
   * @param yAxis The y-axis position of the element.
   * @default 'end'
   */
  public yAxis: InputSignal<TYPosition> = input<TYPosition>('end');

  /**
   * @param show If true, the element will be shown.
   */
  public readonly show: InputSignal<boolean> = input(false);

  /**
   * @param showChange The event emitter for the show signal.
   */
  public readonly showChange = output<boolean>();

  /**
   * @param toggleByAnchor If true, the element will be toggled by the anchor element.
   */
  public readonly toggleByAnchor = input<boolean>(true);

  /**
   * @param distance The distance between the element and the anchor.
   */
  public readonly distance: InputSignal<number> = input<number>(4);

  /**
   * @param closeInternalClick Remove the element when the user clicks inside the element.
   */
  public readonly closeInternalClick: InputSignal<boolean> = input<boolean>(true);

  /**
   * @param disabled If true, the directive will be disabled and no actions will be performed.
   * @default false
   */
  public readonly disabled: InputSignal<boolean> = input<boolean>(false);

  private wasDestroyed = false;
  private trackingFrameId: number | null = null;
  private lastAnchorRect: DOMRect | null = null;
  private lastElSize: { width: number; height: number } | null = null;
  private lastScroll: { x: number; y: number } | null = null;
  private anchorVisibility: IntersectionObserver | null = null;
  /**
   * Documento e janela onde o painel está montado enquanto aberto. São os da
   * âncora, que nem sempre é o global: no mini-monitor destacado (Document
   * Picture-in-Picture) ela está noutra janela, e é no `body`, nos cliques, no
   * scroll e na viewport dela que o painel vive.
   */
  private mountTarget: { doc: Document; view: Window & typeof globalThis } | null = null;

  private get anchorDocument(): Document {
    return documentOf(this.anchor()) ?? documentOf(this.el.nativeElement) ?? document;
  }

  private observeShow = effect(() => {
    const disabled = this.disabled();
    const show = this.show();

    // Desativar com o painel aberto tem de o fechar: sem isto o `stopTracking`
    // ficava inalcançável e o rastreamento por frame corria até ao destroy.
    if (disabled) {
      this.removeItem();
      return;
    }

    if (show) this.addItem();
    else this.removeItem();
  });

  private observeToggleByAnchor = effect(() => {
    const anchor = this.anchor();
    if (!anchor) return;
    // Desativado equivale a não reagir à âncora; deixar os listeners presos
    // manteria referências vivas sem qualquer efeito útil.
    const toggleByAnchor = this.toggleByAnchor() && !this.disabled();

    if (toggleByAnchor) {
      anchor.addEventListener('click', this.addItem, { passive: true });
      anchor.addEventListener('touchstart', this.addItem, { passive: true });
      return;
    }
    anchor.removeEventListener('click', this.addItem);
    anchor.removeEventListener('touchstart', this.addItem);
  });

  public ngOnInit(): void {
    const ref = (documentOf(this.el.nativeElement) ?? document).createElement('div');
    ref.id = this.refToggleElementId;
    this.renderer.addClass(ref, 'hidden');
    this.el.nativeElement.replaceWith(ref);
    this.el.nativeElement.setAttribute('toggle-element-ref', this.refToggleElementId);
    this.renderer.addClass(this.el.nativeElement, 'hidden');
    this.renderer.addClass(this.el.nativeElement, this.zIndexClass() || 'z-[200]');
    this.renderer.addClass(this.el.nativeElement, 'absolute');
  }

  public ngOnDestroy(): void {
    this.wasDestroyed = true;
    this.stopTracking();
    this.removeItem();
  }

  public resize = (): void => {
    if (this.disabled()) return;
    // O reposicionamento depende de innerWidth/innerHeight, que mudam sem a
    // âncora se mexer — daí forçar em vez de confiar na comparação de rects.
    this.updatePosition(true);
  };

  /**
   * Acompanha a âncora frame a frame enquanto o painel está aberto.
   *
   * O `wheel`/`touchmove` que isto substitui só cobre o gesto em si: não dispara
   * durante a inércia do trackpad, nem em scroll por teclado, arrasto da barra,
   * `scrollIntoView` ou reflow de layout. Nesses casos o painel ficava parado e
   * só saltava para o sítio no gesto seguinte.
   */
  private startTracking(): void {
    if (this.trackingFrameId !== null) return;
    this.scheduleTracking();
  }

  private scheduleTracking(): void {
    if (this.wasDestroyed || !this.isMounted) return;

    try {
      // O Firefox lança NS_ERROR_NOT_AVAILABLE num documento já inativo; sem
      // mais frames, o painel morre com a página de qualquer forma.
      this.trackingFrameId = (this.mountTarget?.view ?? window).requestAnimationFrame(() => {
        this.trackingFrameId = null;
        if (this.wasDestroyed || !this.isMounted) return;
        this.updatePosition();
        this.scheduleTracking();
      });
    } catch {
      this.trackingFrameId = null;
    }
  }

  private stopTracking(): void {
    if (this.trackingFrameId !== null) (this.mountTarget?.view ?? window).cancelAnimationFrame(this.trackingFrameId);
    this.trackingFrameId = null;
    this.lastAnchorRect = null;
    this.lastElSize = null;
    this.lastScroll = null;
    this.stopWatchingAnchorVisibility();
  }

  /**
   * Closes the panel once the anchor leaves view. `IntersectionObserver` because the anchor
   * may be clipped by a scrolling ancestor while its rect still sits inside the viewport.
   */
  private watchAnchorVisibility(): void {
    const anchor = this.anchor();
    // O observador da própria janela da âncora: o da página principal não
    // responde por um alvo que está noutra janela.
    const Observer = (this.mountTarget?.view ?? window).IntersectionObserver;

    if (!anchor || typeof Observer === 'undefined') return;

    this.stopWatchingAnchorVisibility();
    this.anchorVisibility = new Observer(
      (entries) => {
        if (entries.every((entry) => entry.isIntersecting)) return;

        this.ngZone.run(() => this.removeItem());
      },
      { threshold: 0 }
    );
    this.anchorVisibility.observe(anchor);
  }

  private stopWatchingAnchorVisibility(): void {
    this.anchorVisibility?.disconnect();
    this.anchorVisibility = null;
  }

  public click = (event: MouseEvent | TouchEvent): void => {
    if (this.disabled()) return;  
    const target = event.target as HTMLElement;
    const toggleElementId = target.closest(`[toggle-element-ref]`)?.getAttribute('toggle-element-ref');
    const toggleElementRef = toggleElementId ? (documentOf(target)?.getElementById(toggleElementId) ?? null) : null;
    const pageY = (event instanceof MouseEvent) ? event.pageY : (event.touches[0]?.pageY || 0);
    const pageX = (event instanceof MouseEvent) ? event.pageX : (event.touches[0]?.pageX || 0);

    const includeEl = (pageY >= this.el.nativeElement.offsetTop &&
      pageY <= this.el.nativeElement.offsetTop + this.el.nativeElement.offsetHeight &&
      pageX >= this.el.nativeElement.offsetLeft &&
      pageX <= this.el.nativeElement.offsetLeft + this.el.nativeElement.offsetWidth) || this.el.nativeElement.contains(target) || this.el.nativeElement.contains(toggleElementRef);

    const includeAnchor = this.anchor() && ((pageY >= this.anchor().offsetTop &&
      pageY <= this.anchor().offsetTop + this.anchor().offsetHeight &&
      pageX >= this.anchor().offsetLeft &&
      pageX <= this.anchor().offsetLeft + this.anchor().offsetWidth) || this.anchor().contains(target)) || this.anchor()?.contains(toggleElementRef);

    if (((this.closeInternalClick() && this.toggleByAnchor()) || !includeEl) && !includeAnchor) {
      this.removeItem();
    }
  };

  /**
   * @param force Recalcula mesmo que a âncora não se tenha mexido (resize da
   * janela, primeira abertura).
   */
  private updatePosition(force = false): void {
    if (this.disabled() || !this.anchor()) return;

    const view = this.mountTarget?.view ?? window;
    const anchorRect = this.anchor().getBoundingClientRect();
    const scrollX = view.scrollX;
    const scrollY = view.scrollY;
    // A altura do painel entra na comparação porque decide o flip vertical, e o
    // scroll da janela porque é somado às coordenadas finais — sem ele, uma
    // âncora `sticky`/`fixed` mantinha o mesmo rect enquanto o painel, que é
    // `absolute` no documento, ficava para trás.
    const elSize = this.el.nativeElement.getBoundingClientRect();
    if (!force && this.isUnchanged(anchorRect, elSize, scrollX, scrollY)) return;
    this.lastAnchorRect = anchorRect;
    this.lastScroll = { x: scrollX, y: scrollY };

    // A escrita da largura invalida o layout, por isso vem antes da leitura do
    // painel e só quando o valor muda — caso contrário cada frame pagava um
    // reflow forçado.
    if (this.el.nativeElement.classList.contains('w-full')) {
      const anchorWidth = `${anchorRect.width}px`;
      if (this.el.nativeElement.style.width !== anchorWidth)
        this.renderer.setStyle(this.el.nativeElement, 'width', anchorWidth);
    }

    // Os rects são lidos uma vez e passados aos cálculos: mover o painel não
    // altera as suas dimensões nem as da âncora.
    const elRect = this.el.nativeElement.getBoundingClientRect();
    const { width, height } = elRect;
    this.lastElSize = { width, height };
    const { innerWidth, innerHeight } = view;

    const xFreePosition = typeof this.xAxis() === 'number';
    const yFreePosition = typeof this.yAxis() === 'number';
    let left = xFreePosition ? this.xAxis() as number : this.calcLeftPosition(this.xAxis(), this.yAxis(), anchorRect, elRect);
    let top = yFreePosition ? this.yAxis() as number : this.calcTopPosition(this.yAxis(), anchorRect, elRect);

    if (top + height > innerHeight) top = yFreePosition ? top - height : this.calcTopPosition('start', anchorRect, elRect);
    if (top < 0) top = yFreePosition ? height : this.calcTopPosition('end', anchorRect, elRect);

    if (left + width > innerWidth) left = xFreePosition ? left - width : this.calcLeftPosition('end', this.yAxis(), anchorRect, elRect);
    if (left < 0) left = xFreePosition ? width : this.calcLeftPosition('start', this.yAxis(), anchorRect, elRect);
    if (left < 0) {
      top = this.calcTopPosition('end', anchorRect, elRect);
      left = this.calcLeftPosition('start', 'end', anchorRect, elRect);
    }

    if (left + width > innerWidth) left = 0;

    // Os cálculos acima estão em coordenadas de viewport (é nelas que o clamp
    // contra innerWidth/innerHeight faz sentido), mas o painel é `position:
    // absolute` dentro do `body`, ou seja, espaço do documento. Na shell atual o
    // `body` não rola e o offset é 0; explicitá-lo evita a deriva silenciosa em
    // qualquer layout onde passe a rolar.
    this.setCoordinate('top', top + scrollY);
    this.setCoordinate('left', left + scrollX);
  }

  /**
   * Evita escritas redundantes: reatribuir o mesmo valor a `style.top`/`left`
   * marca o elemento como sujo e força o browser a repintar sem necessidade.
   */
  private setCoordinate(property: 'top' | 'left', value: number): void {
    const next = `${value}px`;
    if (this.el.nativeElement.style[property] === next) return;
    this.renderer.setStyle(this.el.nativeElement, property, next);
  }

  /**
   * O reposicionamento é dispensável apenas quando *nada* do que entra no
   * cálculo mudou: o rect da âncora, as dimensões do painel (decidem os flips) e
   * o scroll da janela (é somado às coordenadas finais).
   */
  private isUnchanged(anchorRect: DOMRect, elRect: DOMRect, scrollX: number, scrollY: number): boolean {
    const previousAnchor = this.lastAnchorRect;
    const previousSize = this.lastElSize;
    const previousScroll = this.lastScroll;
    if (!previousAnchor || !previousSize || !previousScroll) return false;
    return (
      anchorRect.top === previousAnchor.top &&
      anchorRect.left === previousAnchor.left &&
      anchorRect.width === previousAnchor.width &&
      anchorRect.height === previousAnchor.height &&
      elRect.width === previousSize.width &&
      elRect.height === previousSize.height &&
      scrollX === previousScroll.x &&
      scrollY === previousScroll.y
    );
  }

  /**
   * `body.contains(el)` responde «é descendente?», não «é filho direto?». Como
   * o elemento é montado com `body.appendChild`, só o teste de pai direto
   * distingue «já está montado» de «continua na posição original do template»
   * — e é esse o teste que o `removeChild` exige.
   */
  private get isMounted(): boolean {
    return !!this.mountTarget && this.el.nativeElement.parentNode === this.mountTarget.doc.body;
  }

  private addItem = (): void => {
    if (this.disabled()) return;

    const doc = this.anchorDocument;
    if (!doc.body || this.isMounted) return;
    const view = doc.defaultView ?? window;
    this.mountTarget = { doc, view };
    this.ngZone.runOutsideAngular(() => {
      doc.addEventListener('touchcancel', this.click, { passive: true });
      view.addEventListener('resize', this.resize, { passive: true });
    });
    doc.addEventListener('click', this.click, { passive: true });
    doc.body.appendChild(this.el.nativeElement);
    this.showChange.emit(true);

    this.renderer.removeClass(this.el.nativeElement, 'hidden');
    this.updatePosition(true);
    // O rastreamento corre fora do Angular: são leituras de layout e escritas
    // de estilo por frame, nada que deva agendar deteção de alterações.
    this.ngZone.runOutsideAngular(() => {
      this.startTracking();
      this.watchAnchorVisibility();
    });
    this.el.nativeElement.animate(
      [
        { transform: 'scale(0.9)', opacity: 0 },
        { transform: 'scale(1)', opacity: 1 },
      ],
      {
        duration: 150,
        easing: 'ease',
      }
    );
  };

  private removeItem(): void {
    if (!this.mountTarget || !this.isMounted) return;
    const { doc, view } = this.mountTarget;
    this.stopTracking();
    doc.removeEventListener('touchcancel', this.click);
    doc.removeEventListener('click', this.click);
    view.removeEventListener('resize', this.resize);
    this.mountTarget = null;
    // `remove()` desprende o elemento do pai que ele realmente tiver e nunca
    // lança, ao contrário de `body.removeChild()`, que exige filho direto.
    this.el.nativeElement.remove();
    this.renderer.addClass(this.el.nativeElement, 'hidden');
    if (!this.wasDestroyed) this.showChange.emit(false);
  }

  private calcTopPosition(position: TYPosition, anchorRect: DOMRect, elRect: DOMRect): number {
    const { top, height } = anchorRect;
    const { height: elHeight } = elRect;
    const distance = this.distance();

    switch (position) {
      case 'start':
        return top - elHeight - distance;
      case 'center-start':
        return top;
      case 'center':
        return top + height / 2 - elHeight / 2;
      case 'center-end':
        return top + height - elHeight;
      case 'end':
        return top + height + distance;
      default:
        return top - elHeight - distance;
    }
  }

  private calcLeftPosition(positionX: TXPosition, positionY: TYPosition, anchorRect: DOMRect, elRect: DOMRect): number {
    const { left, width } = anchorRect;
    const { width: elWidth } = elRect;
    const distance = this.distance();

    const applyHorizontalDistance = positionY === 'center' || positionY === 'center-start' || positionY === 'center-end';

    switch (positionX) {
      case 'start':
        return applyHorizontalDistance ? left - elWidth - distance : left;
      case 'center':
        return left + width / 2 - elWidth / 2;
      case 'end':
        return applyHorizontalDistance ? left + width + distance : left + width - elWidth;
      default:
        return applyHorizontalDistance ? left - elWidth - distance : left;
    }
  }
}